import { ThemeEvents } from '@theme/events';

/**
 * Lightweight GA4 ecommerce bridge for the product page. Reads the server-rendered
 * product data island and pushes view_item / select_item / add_to_cart to the GTM
 * dataLayer by listening to the theme's own events. Non-blocking; no dependencies.
 */

const dataElement = document.getElementById('analytics-product-data');

/** @type {{ id: number, title: string, vendor: string, currency: string, variants: { id: number, title: string, price: number, sku: string }[] } | null} */
let product = null;
try {
  product = dataElement ? JSON.parse(dataElement.textContent || 'null') : null;
} catch (error) {
  product = null;
}

/**
 * Pushes an event to the dataLayer, clearing the previous ecommerce object first
 * (GA4 best practice to avoid objects merging across events).
 * @param {Record<string, unknown>} payload
 */
function push(payload) {
  window.dataLayer = window.dataLayer || [];
  if ('ecommerce' in payload) window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(payload);
}

/**
 * @param {string | number | null} id
 * @returns {{ id: number, title: string, price: number, sku: string } | null}
 */
function variantById(id) {
  if (!product || id == null) return null;
  return product.variants.find((variant) => String(variant.id) === String(id)) ?? null;
}

/** @returns {string | null} The currently-selected variant id from the product form or URL. */
function currentVariantId() {
  const input = document.querySelector('product-form-component form input[name="id"]');
  if (input instanceof HTMLInputElement && input.value) return input.value;
  return new URL(window.location.href).searchParams.get('variant');
}

function currentVariant() {
  return variantById(currentVariantId()) ?? product?.variants?.[0] ?? null;
}

/**
 * @param {{ id?: number, title?: string, price?: number } | null} variant
 * @param {number} quantity
 */
function toItem(variant, quantity) {
  return {
    item_id: product?.id,
    item_name: product?.title,
    item_brand: product?.vendor,
    item_variant: variant?.title,
    price: variant?.price,
    quantity: quantity || 1,
  };
}

if (product) {
  const initialVariant = currentVariant();
  push({
    event: 'view_item',
    ecommerce: {
      currency: product.currency,
      value: initialVariant?.price,
      items: [toItem(initialVariant, 1)],
    },
  });

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const resource = /** @type {any} */ (event).detail?.resource;
    if (!resource) return;

    const variant =
      variantById(resource.id) ??
      (typeof resource.price === 'number'
        ? { id: resource.id, title: resource.title, price: resource.price / 100, sku: resource.sku ?? '' }
        : null);

    push({
      event: 'select_item',
      ecommerce: {
        currency: product.currency,
        items: [toItem(variant, 1)],
      },
    });
  });

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const data = /** @type {any} */ (event).detail?.data ?? {};
    if (data.source && data.source !== 'product-form-component') return;

    const variant = variantById(data.variantId) ?? currentVariant();
    const quantity = Number(data.itemCount) || 1;

    push({
      event: 'add_to_cart',
      ecommerce: {
        currency: product.currency,
        value: (variant?.price ?? 0) * quantity,
        items: [toItem(variant, quantity)],
      },
    });
  });
}
